/**
 * Admin -> Sheets import (eskiz 25): bir martalik ko'chirish, 4 qadam.
 * 1 ulash -> 2 ustunlarni moslash -> 3 tekshirish va dublikatlar -> 4 import. Import tugaguncha hech narsa yozilmaydi.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Download, FileSpreadsheet, Link2, Upload } from "lucide-react";
import { ApiError } from "@/lib/api";
import { cn, formatWhen } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { useUserOptions } from "@/features/drivers/api";
import { useBranches } from "@/features/admin/api";
import {
  errorsCsvUrl,
  templateCsvUrl,
  useCreateImport,
  useImportJob,
  useImports,
  usePreviewImport,
  useRunImport,
  useUpdateImport,
  type ImportJob,
  type ImportStats,
  type OwnerStrategy,
} from "@/features/imports/api";
import { useGoogleStatus } from "@/features/integrations/api";
import { importMenu } from "@/features/imports/menu";
import { ContextMenu } from "@/components/ui/context-menu";
import { copyText } from "@/lib/clipboard";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Field, Input, Select } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import { EmptyState, PageLoading, Spinner } from "@/components/ui/misc";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";

type Step = 1 | 2 | 3 | 4;
// label — tarjima kaliti: import:steps.<label>
const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: "connect" },
  { n: 2, label: "map" },
  { n: 3, label: "preview" },
  { n: 4, label: "import" },
];

/** Job holatidan qaysi qadamda ekanini aniqlash (oldingi importni ochganda) */
function stepForJob(job: ImportJob): Step {
  switch (job.status) {
    case "uploaded":
    case "mapped":
      return 2;
    case "previewed":
      return 3;
    case "importing":
    case "done":
    case "failed":
      return 4;
    default:
      return 1;
  }
}

export function ImportPage() {
  const { t } = useTranslation("import");
  const me = useMe();
  if (!me.data) return <PageLoading />;
  if (me.data.role !== "owner") {
    return (
      <AdminLayout title={t("title")}>
        <EmptyState title={t("ownerOnly")} description={t("ownerOnlyDesc")} />
      </AdminLayout>
    );
  }
  return <ImportWizard />;
}

function ImportWizard() {
  const { t } = useTranslation("import");
  const [jobId, setJobId] = useState<number | null>(null);
  const [step, setStep] = useState<Step>(1);
  const job = useImportJob(jobId);

  function openJob(j: ImportJob) {
    setJobId(j.id);
    setStep(stepForJob(j));
  }

  function reset() {
    setJobId(null);
    setStep(1);
  }

  return (
    <AdminLayout title={t("title")} subtitle={t("subtitle")}>
      <Stepper current={step} />
      {step === 1 && <ConnectStep onConnected={openJob} />}
      {step !== 1 && jobId !== null && !job.data && <PageLoading />}
      {step === 2 && job.data && <MapStep job={job.data} onBack={reset} onPreviewed={(j) => { openJob(j); setStep(3); }} />}
      {step === 3 && job.data && <PreviewStep job={job.data} onBack={() => setStep(2)} onDone={() => setStep(4)} />}
      {step === 4 && job.data && <DoneStep job={job.data} onBack={() => setStep(3)} onRestart={reset} />}
    </AdminLayout>
  );
}

function Stepper({ current }: { current: Step }) {
  const { t } = useTranslation("import");
  return (
    <div className="mb-5 flex items-center gap-2">
      {STEPS.map((s, i) => {
        const done = s.n < current;
        const active = s.n === current;
        return (
          <div key={s.n} className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-bold",
                  done && "bg-success text-white",
                  active && "bg-primary text-white",
                  !done && !active && "border border-border bg-surface text-muted",
                )}
              >
                {done ? <Check size={14} /> : s.n}
              </span>
              <span className={cn("text-[13px]", active ? "font-semibold" : done ? "text-ink" : "text-muted")}>{t(`steps.${s.label}`)}</span>
            </div>
            {i < STEPS.length - 1 && <span className={cn("mx-2 h-px w-16", done ? "bg-success" : "bg-line")} />}
          </div>
        );
      })}
    </div>
  );
}

// ---- 1. Ulash ----

function ConnectStep({ onConnected }: { onConnected: (job: ImportJob) => void }) {
  const { t } = useTranslation("import");
  const { t: tm } = useTranslation("menu");
  const create = useCreateImport();
  const imports = useImports();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetTab, setSheetTab] = useState("");
  const [fileError, setFileError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  // Service account emailini oldingi importlardan olamiz (har job'da bir xil)
  const serviceEmail = imports.data?.results[0]?.service_account_email ?? "";

  async function submitFile(f: File | null) {
    if (!f) return;
    setFileError(null);
    try {
      const job = await create.mutateAsync({ file: f });
      toast.success(t("connect.fileRead"), t("connect.fileReadDesc", { rows: job.row_count, cols: job.columns.length }));
      onConnected(job);
    } catch (e) {
      setFileError(e instanceof ApiError ? (e.fields?.file?.join(" ") ?? e.message) : t("connect.fileFailed"));
    }
  }

  async function submitUrl() {
    setUrlError(null);
    if (!sheetUrl.trim()) {
      setUrlError(t("connect.pasteLink"));
      return;
    }
    try {
      const job = await create.mutateAsync({ sheet_url: sheetUrl.trim(), sheet_tab: sheetTab.trim() });
      toast.success(t("connect.sheetConnected"), t("connect.sheetConnectedDesc", { rows: job.row_count }));
      onConnected(job);
    } catch (e) {
      setUrlError(e instanceof ApiError ? e.message : t("connect.sheetFailed"));
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader title={t("connect.uploadTitle")} subtitle={t("connect.uploadDesc")} />
          <CardBody>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0] ?? null;
                setFile(f);
              }}
              onClick={() => fileRef.current?.click()}
              className={cn(
                "flex cursor-pointer flex-col items-center justify-center rounded-[10px] border border-dashed px-4 py-8 text-center",
                dragOver ? "border-primary bg-primary-soft/40" : "border-border bg-fill/40",
              )}
            >
              <Upload size={22} className="mb-2 text-muted" />
              {file ? (
                <div className="text-[13px]">
                  <span className="font-semibold">{file.name}</span>
                  <span className="text-muted"> · {(file.size / 1024).toFixed(0)} KB</span>
                </div>
              ) : (
                <div className="text-[13px]">
                  {t("connect.dropHere")} <span className="text-primary">{t("connect.browse")}</span>
                </div>
              )}
              <div className="mt-1 text-[11px] text-muted">{t("connect.limit")}</div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xlsm,.tsv,.txt"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
            {fileError && <p className="mt-2 text-[12px] text-danger">{fileError}</p>}
            <p className="mt-3 text-[12px] text-muted">{t("connect.howTo")}</p>
            <div className="mt-4 flex items-center justify-between">
              <a href={templateCsvUrl} className="inline-flex items-center gap-1 text-[12px] text-primary hover:underline">
                <Download size={13} /> {t("connect.template")}
              </a>
              <Button variant="primary" disabled={!file} loading={create.isPending && !!file} onClick={() => submitFile(file)}>
                {t("connect.readFile")}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t("connect.linkTitle")} subtitle={t("connect.linkDesc")} />
          <CardBody className="space-y-3">
            <Field label={t("connect.sheetUrl")} error={urlError ?? undefined}>
              <Input value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" />
            </Field>
            <Field label={t("connect.tabName")} hint={t("connect.tabHint")}>
              <Input value={sheetTab} onChange={(e) => setSheetTab(e.target.value)} placeholder="Sheet1" />
            </Field>
            <SheetAccess serviceEmail={serviceEmail} />
            <div className="flex justify-end">
              <Button variant="primary" disabled={!sheetUrl.trim()} loading={create.isPending && !file} onClick={submitUrl}>
                {t("connect.connectSheet")}
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title={t("connect.previous")} />
        <CardBody>
          {imports.isPending ? (
            <Spinner />
          ) : !imports.data || imports.data.results.length === 0 ? (
            <p className="text-[13px] text-muted">{t("connect.noImports")}</p>
          ) : (
            <Table variant="bare">
              <thead>
                <tr>
                  <Th>{t("connect.col.file")}</Th>
                  <Th>{t("connect.col.when")}</Th>
                  <Th>{t("connect.col.who")}</Th>
                  <Th>{t("connect.col.status")}</Th>
                  <Th className="text-right">{t("connect.col.rows")}</Th>
                  <Th className="text-right">{t("connect.col.imported")}</Th>
                  <Th className="text-right">{t("connect.col.errors")}</Th>
                </tr>
              </thead>
              <tbody>
                {imports.data.results.map((j) => (
                  <ContextMenu key={j.id} items={() => importMenu(j, { open: () => onConnected(j), openUrl: (url) => window.open(url, "_blank", "noopener,noreferrer"), copy: (text) => void copyText(text) }, tm)}>
                    <Tr clickable onClick={() => onConnected(j)}>
                      <Td>
                        <span className="inline-flex items-center gap-1.5 font-semibold">
                          <FileSpreadsheet size={14} className="text-muted" />
                          {j.file_name || j.sheet_url || t("connect.importN", { id: j.id })}
                        </span>
                      </Td>
                      <Td>{formatWhen(j.created_at)}</Td>
                      <Td>{j.created_by_name ?? "—"}</Td>
                      <Td>
                        <ImportStatusPill status={j.status} />
                      </Td>
                      <Td className="text-right">{j.row_count}</Td>
                      <Td className="text-right">{j.imported_count}</Td>
                      <Td className="text-right">{j.error_rows.length}</Td>
                    </Tr>
                  </ContextMenu>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ImportStatusPill({ status }: { status: ImportJob["status"] }) {
  const { t } = useTranslation("import");
  const tone = status === "done" ? "green" : status === "failed" ? "red" : status === "importing" ? "orange" : "grey";
  return <Badge tone={tone}>{t(`status.${status}`)}</Badge>;
}

// ---- 2. Ustunlarni moslash ----

function MapStep({ job, onBack, onPreviewed }: { job: ImportJob; onBack: () => void; onPreviewed: (job: ImportJob) => void }) {
  const { t } = useTranslation("import");
  const update = useUpdateImport();
  const preview = usePreviewImport();
  const [mapping, setMapping] = useState<Record<string, string>>(job.mapping);
  const [statusMap, setStatusMap] = useState<Record<string, string>>(job.status_map);
  const [strategy, setStrategy] = useState<OwnerStrategy>(job.owner_strategy);
  const [defaultOwner, setDefaultOwner] = useState<number | null>(job.default_owner);
  const [defaultBranch, setDefaultBranch] = useState<number | null>(job.default_branch);
  const [defaultSource, setDefaultSource] = useState(job.default_source);
  const [error, setError] = useState<string | null>(null);

  // Server status qiymatlarini qayta hisoblasa (ustun o'zgarganda) lokal xaritani to'ldiramiz
  useEffect(() => {
    setStatusMap((prev) => ({ ...job.status_map, ...prev }));
  }, [job.status_map]);

  const recruiters = useUserOptions({ role: "recruiter" });
  const branches = useBranches();

  const phoneMapped = Object.values(mapping).includes("phone");
  const hasStatusColumn = Object.values(mapping).includes("status");
  const hasOwnerColumn = Object.values(mapping).includes("owner");
  const unmappedStatuses = job.status_values.filter((v) => !statusMap[v]);
  const usedTargets = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const tg of Object.values(mapping)) if (tg !== "skip") counts[tg] = (counts[tg] ?? 0) + 1;
    return counts;
  }, [mapping]);
  const duplicateTargets = Object.entries(usedTargets).filter(([, n]) => n > 1).map(([tg]) => tg);

  async function saveAndPreview() {
    setError(null);
    if (!phoneMapped) {
      setError(t("map.phoneRequired"));
      return;
    }
    if (duplicateTargets.length > 0) {
      setError(t("map.usedTwice", { fields: duplicateTargets.join(", ") }));
      return;
    }
    try {
      await update.mutateAsync({
        id: job.id,
        mapping,
        status_map: statusMap,
        owner_strategy: strategy,
        default_owner: strategy === "pool" ? null : defaultOwner,
        default_branch: strategy === "pool" ? defaultBranch : null,
        default_source: defaultSource,
      });
      const previewed = await preview.mutateAsync(job.id);
      onPreviewed(previewed);
    } catch (e) {
      const msg = e instanceof ApiError ? (e.fields?.mapping?.join(" ") ?? e.message) : t("map.saveFailed");
      setError(msg);
      toast.error(msg);
    }
  }

  const sample = job.sample_rows[0] ?? [];

  return (
    <div className="space-y-4">
      <p className="text-[12px] text-muted">
        {t("map.connected")} <span className="font-semibold text-ink">{job.file_name || job.sheet_url}</span> · {t("map.rowsCols", { rows: job.row_count, cols: job.columns.length })}
        {job.sheet_tabs.length > 1 && <> · {t("map.tabs", { tabs: job.sheet_tabs.join(", ") })}</>}
      </p>

      <Card>
        <CardHeader title={t("map.columnMapping")} subtitle={t("map.step2")} />
        <CardBody>
          <Table variant="bare">
            <thead>
              <tr>
                <Th>{t("map.col.sheet")}</Th>
                <Th>{t("map.col.sample")}</Th>
                <Th>{t("map.col.field")}</Th>
                <Th>{t("map.col.req")}</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {job.columns.map((col, i) => {
                const target = mapping[col] ?? "skip";
                const twice = target !== "skip" && usedTargets[target] > 1;
                return (
                  <Tr key={col}>
                    <Td className="font-medium">{col}</Td>
                    <Td className="max-w-[220px] truncate text-ink-soft" title={sample[i]}>
                      {sample[i] || <span className="text-muted">—</span>}
                    </Td>
                    <Td>
                      <Select value={target} onChange={(e) => setMapping({ ...mapping, [col]: e.target.value })} className="max-w-[240px]" aria-label={t("map.fieldFor", { col })}>
                        {job.targets.map((tg) => (
                          <option key={tg.key} value={tg.key}>
                            {tg.label}
                          </option>
                        ))}
                      </Select>
                    </Td>
                    <Td className="text-muted">{target === "phone" ? t("map.yes") : ""}</Td>
                    <Td>
                      {twice ? (
                        <span className="text-[12px] font-semibold text-danger">{t("map.twice")}</span>
                      ) : target === "skip" ? (
                        <span className="text-[12px] text-muted">{t("map.skipped")}</span>
                      ) : (
                        <span className="text-[12px] text-success">{t("map.ok")}</span>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          {!phoneMapped && <p className="mt-2 text-[12px] font-semibold text-danger">{t("map.phoneHint")}</p>}
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader
            title={t("map.statusValues")}
            subtitle={hasStatusColumn ? t("map.statusSubtitle", { n: job.status_values.length, unmapped: unmappedStatuses.length }) : t("map.noStatusColumn")}
          />
          <CardBody>
            {hasStatusColumn && job.status_values.length > 0 ? (
              <div className="max-h-[320px] overflow-y-auto">
                <Table variant="bare">
                  <thead>
                    <tr>
                      <Th>{t("map.inSheet")}</Th>
                      <Th>{t("map.inSystem")}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.status_values.map((raw) => (
                      <Tr key={raw}>
                        <Td className="font-mono text-[12px]">{raw}</Td>
                        <Td>
                          <Select value={statusMap[raw] ?? "new"} onChange={(e) => setStatusMap({ ...statusMap, [raw]: e.target.value })} className="max-w-[220px]" aria-label={t("map.statusFor", { raw })}>
                            {job.statuses.map((s) => (
                              <option key={s.key} value={s.key}>
                                {t(`common:status.${s.key}`, { defaultValue: s.label })}
                              </option>
                            ))}
                          </Select>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p className="text-[13px] text-muted">{t("map.mapStatusHint")}</p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t("map.owner")} subtitle={t("map.ownerDesc")} />
          <CardBody className="space-y-3">
            <RadioRow
              checked={strategy === "column"}
              onChange={() => setStrategy("column")}
              label={t("map.useColumn")}
              hint={hasOwnerColumn ? t("map.useColumnHint") : t("map.useColumnMissing")}
            />
            <RadioRow checked={strategy === "single"} onChange={() => setStrategy("single")} label={t("map.single")} />
            <RadioRow checked={strategy === "pool"} onChange={() => setStrategy("pool")} label={t("map.pool")} hint={t("map.poolHint")} />
            {strategy !== "pool" && (
              <Field label={strategy === "single" ? t("map.recruiter") : t("map.defaultOwner")}>
                <Select value={defaultOwner ?? ""} onChange={(e) => setDefaultOwner(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">{t("map.nonePool")}</option>
                  {recruiters.data?.results.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name} · {u.branch_name ?? "—"}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            {strategy === "pool" && (
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("map.branch")}>
                  <Select value={defaultBranch ?? ""} onChange={(e) => setDefaultBranch(e.target.value ? Number(e.target.value) : null)}>
                    <option value="">{t("map.choose")}</option>
                    {branches.data?.results.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            )}
            <Field label={t("map.defaultSource")} hint={t("map.defaultSourceHint")}>
              <Select value={defaultSource} onChange={(e) => setDefaultSource(e.target.value)}>
                {job.sources.map((s) => (
                  <option key={s.key} value={s.key}>
                    {t(`common:source.${s.key}`, { defaultValue: s.label })}
                  </option>
                ))}
              </Select>
            </Field>
          </CardBody>
        </Card>
      </div>

      {error && <p className="text-[12px] font-semibold text-danger">{error}</p>}
      <div className="flex items-center justify-between border-t border-line pt-4">
        <p className="text-[12px] text-muted">{t("map.nothingWritten")}</p>
        <div className="flex gap-2">
          <Button onClick={onBack}>{t("map.back")}</Button>
          <Button variant="primary" loading={update.isPending || preview.isPending} onClick={saveAndPreview}>
            {t("map.savePreview")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RadioRow({ checked, onChange, label, hint }: { checked: boolean; onChange: () => void; label: string; hint?: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-[13px]">
      <input type="radio" checked={checked} onChange={onChange} className="mt-0.5 accent-primary" />
      <span>
        <span className={checked ? "font-semibold" : ""}>{label}</span>
        {hint && <span className="block text-[11px] text-muted">{hint}</span>}
      </span>
    </label>
  );
}

// ---- 3. Tekshirish ----

function PreviewStep({ job, onBack, onDone }: { job: ImportJob; onBack: () => void; onDone: () => void }) {
  const { t } = useTranslation("import");
  const run = useRunImport();
  const stats = job.stats as ImportStats;
  const [error, setError] = useState<string | null>(null);
  const valid = stats.valid ?? 0;
  const mapped = job.columns.filter((c) => (job.mapping[c] ?? "skip") !== "skip");
  const targetLabel = (key: string) => job.targets.find((tg) => tg.key === key)?.label ?? key;

  async function doImport() {
    setError(null);
    try {
      const res = await run.mutateAsync(job.id);
      toast.success(t("preview.imported", { n: res.imported }));
      onDone();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : t("preview.failed");
      setError(msg);
      toast.error(t("preview.failedTitle"), msg);
    }
  }

  const reasons = Object.entries(stats.error_reasons ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_420px]">
        <Card>
          <CardHeader title={t("map.columnMapping")} subtitle={t("preview.step2done")} />
          <CardBody>
            <Table variant="bare">
              <thead>
                <tr>
                  <Th>{t("map.col.sheet")}</Th>
                  <Th>{t("map.col.field")}</Th>
                </tr>
              </thead>
              <tbody>
                {mapped.map((c) => (
                  <Tr key={c}>
                    <Td>{c}</Td>
                    <Td className="font-medium">{targetLabel(job.mapping[c])}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <p className="mt-3 text-[12px] text-muted">
              {t("preview.ownerFrom")} {job.owner_strategy === "column" ? t("preview.ownerColumn") : job.owner_strategy === "single" ? t("preview.ownerSingle") : t("preview.ownerPool")}
              {typeof stats.owners_unresolved === "number" && stats.owners_unresolved > 0 && (
                <span className="text-warning"> · {t("preview.unresolved", { n: stats.owners_unresolved })}</span>
              )}
              {stats.status_unmapped && stats.status_unmapped.length > 0 && <span className="text-warning"> · {t("preview.statusUnmapped", { n: stats.status_unmapped.length })}</span>}
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t("preview.title")} subtitle={t("preview.step3")} />
          <CardBody>
            <StatRow label={t("preview.rowsFound")} value={stats.rows ?? 0} />
            <StatRow label={t("preview.ready")} value={valid} tone="success" />
            <StatRow label={t("preview.duplicates")} value={stats.duplicates ?? 0} />
            <StatRow label={t("preview.errors")} value={stats.errors ?? 0} tone={(stats.errors ?? 0) > 0 ? "danger" : undefined} />
            <StatRow label={t("preview.reformatted")} value={stats.reformatted_phones ?? 0} />
            {reasons.length > 0 && (
              <div className="mt-3 rounded-[10px] border border-danger bg-danger-soft px-3 py-2 text-[12px]">
                <div className="font-semibold">{t("preview.whyFail", { n: stats.errors })}</div>
                {reasons.map(([reason, n]) => (
                  <div key={reason}>
                    {reason} · {n}
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-[11px] text-muted">{t("preview.dupHint")}</p>
            {job.error_rows.length > 0 && (
              <a href={errorsCsvUrl(job.id)} className="mt-3 inline-flex items-center gap-1 text-[12px] text-primary hover:underline">
                <Download size={13} /> {t("preview.downloadErrors")}
              </a>
            )}
          </CardBody>
        </Card>
      </div>

      {job.error_rows.length > 0 && (
        <Card>
          <CardHeader title={t("preview.notImported", { n: job.error_rows.length })} subtitle={t("preview.notImportedDesc")} />
          <CardBody>
            <Table variant="bare">
              <thead>
                <tr>
                  <Th>{t("preview.col.row")}</Th>
                  <Th>{t("preview.col.reason")}</Th>
                  <Th>{t("preview.col.values")}</Th>
                </tr>
              </thead>
              <tbody>
                {job.error_rows.slice(0, 20).map((e) => (
                  <Tr key={`${e.row}-${e.reason}`}>
                    <Td className="text-muted">{e.row}</Td>
                    <Td className={cn("font-medium", e.duplicate ? "text-ink-soft" : "text-danger")}>{e.reason}</Td>
                    <Td className="max-w-[640px] truncate text-[12px] text-ink-soft" title={e.raw.join(" | ")}>
                      {e.raw.filter(Boolean).join(" · ")}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {error && <p className="text-[12px] font-semibold text-danger">{error}</p>}
      <div className="flex items-center justify-between border-t border-line pt-4">
        <p className="text-[12px] text-muted">{t("preview.transaction")}</p>
        <div className="flex gap-2">
          <Button onClick={onBack} disabled={run.isPending}>
            {t("map.back")}
          </Button>
          <Button variant="primary" loading={run.isPending} disabled={valid === 0} onClick={doImport}>
            {run.isPending ? t("preview.importing") : t("preview.importN", { n: valid })}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, value, tone }: { label: string; value: number | string; tone?: "success" | "danger" }) {
  return (
    <div className="flex items-center justify-between border-b border-line py-2 text-[13px] last:border-b-0">
      <span>{label}</span>
      <span className={cn("text-[15px] font-bold", tone === "success" && "text-success", tone === "danger" && "text-danger")}>{typeof value === "number" ? value.toLocaleString("en-US") : value}</span>
    </div>
  );
}

// ---- 4. Natija ----

function DoneStep({ job, onBack, onRestart }: { job: ImportJob; onBack: () => void; onRestart: () => void }) {
  const { t } = useTranslation("import");
  const stats = job.stats as ImportStats;
  if (job.status === "importing") {
    return (
      <Card>
        <CardBody className="flex items-center gap-3 py-8">
          <Spinner /> <span className="text-[13px]">{t("done.importing")}</span>
        </CardBody>
      </Card>
    );
  }
  if (job.status === "failed") {
    return (
      <Card>
        <CardBody className="py-6">
          <div className="text-[15px] font-semibold text-danger">{t("done.failed")}</div>
          <p className="mt-1 text-[13px] text-ink-soft">{job.error_message || t("done.unknown")}</p>
          <div className="mt-4 flex gap-2">
            <Button onClick={onBack}>{t("done.backToPreview")}</Button>
            <Button variant="primary" onClick={onRestart}>
              {t("done.another")}
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }
  return (
    <Card>
      <CardBody className="py-6">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-success-soft text-success">
            <Check size={20} />
          </span>
          <div>
            <div className="text-[18px] font-bold">{t("done.imported", { n: job.imported_count.toLocaleString("en-US") })}</div>
            <div className="text-[12px] text-muted">
              {job.file_name || job.sheet_url} · {job.finished_at ? formatWhen(job.finished_at) : ""} · {t("done.history")}
            </div>
          </div>
        </div>
        <div className="mt-4 grid max-w-[520px] grid-cols-2 gap-x-8">
          <StatRow label={t("done.rowsFound")} value={stats.rows ?? job.row_count} />
          <StatRow label={t("done.importedLabel")} value={job.imported_count} tone="success" />
          <StatRow label={t("done.duplicates")} value={stats.duplicates ?? 0} />
          <StatRow label={t("done.errors")} value={stats.errors ?? 0} tone={(stats.errors ?? 0) > 0 ? "danger" : undefined} />
        </div>
        {job.error_rows.length > 0 && (
          <a href={errorsCsvUrl(job.id)} className="mt-3 inline-flex items-center gap-1 text-[12px] text-primary hover:underline">
            <Download size={13} /> {t("preview.downloadErrors")}
          </a>
        )}
        <div className="mt-5 flex gap-2">
          <Link to="/drivers?source=import">
            <Button variant="primary">{t("done.goDrivers")}</Button>
          </Link>
          <Button onClick={onRestart}>{t("done.another")}</Button>
        </div>
      </CardBody>
    </Card>
  );
}

/** Jadval kim nomidan o'qiladi: ulangan Google akkaunt (asosiy yo'l) yoki service account */
function SheetAccess({ serviceEmail }: { serviceEmail: string }) {
  const { t } = useTranslation("import");
  const google = useGoogleStatus().data;
  const settingsLink = "/admin/settings?section=integrations";
  if (google?.status === "connected") {
    return (
      <div className="rounded-[10px] border border-success bg-success-soft px-3 py-2 text-[12px]">
        <div className="flex items-center gap-1.5 font-semibold">
          <Check size={13} className="text-success" />
          {t("connect.googleConnected", { email: google.email })}
        </div>
        <div className="mt-0.5 text-ink-soft">{t("connect.googleConnectedHint")}</div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {google?.status === "needs_reauth" ? (
        <div className="flex items-start gap-1.5 rounded-[10px] border border-danger bg-danger-soft px-3 py-2 text-[12px]">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-danger" />
          <span>
            {t("connect.googleReauth")}{" "}
            <Link to={settingsLink} className="text-primary hover:underline">
              {t("connect.googleReconnectLink")}
            </Link>
          </span>
        </div>
      ) : google?.configured ? (
        <div className="rounded-[10px] border border-line bg-fill/50 px-3 py-2 text-[12px]">
          {t("connect.googleNotConnected")}{" "}
          <Link to={settingsLink} className="text-primary hover:underline">
            {t("connect.googleConnectLink")}
          </Link>
        </div>
      ) : null}
      {serviceEmail ? (
        <div className="rounded-[10px] border border-line bg-fill/50 px-3 py-2 text-[12px]">
          <Link2 size={12} className="mr-1 inline text-muted" />
          {google?.configured ? t("connect.googleOrShare") : t("connect.shareWith")} <span className="font-mono font-semibold">{serviceEmail}</span>
        </div>
      ) : !google?.configured ? (
        <p className="text-[12px] text-muted">{t("connect.notConfigured")}</p>
      ) : null}
    </div>
  );
}
